/**
 * ParentMessages — the "Messages" tab in the parent portal.
 *
 * Lets a parent:
 *   - See their message threads for the selected child
 *   - Start a new thread with a category template
 *   - For "Update student details", submit a structured change request
 *     (whitelisted fields only) that school staff approve
 *   - Reply within an open thread (two-way)
 *
 * Security: all reads/writes are scoped by Firestore rules to threads
 * where parentUid == the signed-in parent AND studentId is one of
 * their linked children. This component assumes the child passed in
 * is already one of the parent's linked children (the portal enforces
 * that upstream).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import {
  MessageSquare,
  Plus,
  Send,
  ChevronLeft,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Pencil,
} from 'lucide-react';
import { db, auth } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { SoftCard, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { Button } from '@/components/ui/primitives';
import type { Student, Subject } from '@/types';
import {
  CATEGORY_LABELS,
  STATUS_LABELS,
  CHANGEABLE_FIELDS,
  getChangeableField,
  type MessageCategory,
  type ParentMessageThread,
  type ParentMessagePost,
  type ProposedChange,
} from './types';

interface ParentMessagesProps {
  child: Student;
  subjects: Subject[];
}

export default function ParentMessages({ child, subjects }: ParentMessagesProps) {
  const { userData } = useAuth();
  const [threads, setThreads] = useState<ParentMessageThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'compose' | 'thread'>('list');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const parentUid = userData?.uid ?? auth.currentUser?.uid ?? '';

  // Subjects the child is enrolled in (fallback: all subjects)
  const childSubjects = useMemo(() => {
    const ids = child.subjects ?? [];
    const enrolled = subjects.filter((s) => ids.includes(s.id));
    return enrolled.length > 0 ? enrolled : subjects;
  }, [child.subjects, subjects]);

  /* ---------- load this parent's threads for this child ---------- */
  useEffect(() => {
    if (!parentUid) return;
    setLoading(true);
    const q = query(
      collection(db, 'reportParentMessages'),
      where('parentUid', '==', parentUid),
      where('studentId', '==', child.id),
      orderBy('lastMessageAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const out: ParentMessageThread[] = [];
        snap.forEach((d) =>
          out.push({ id: d.id, ...(d.data() as Omit<ParentMessageThread, 'id'>) }),
        );
        setThreads(out);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [parentUid, child.id]);

  const activeThread = useMemo(
    () => threads.find((t) => t.id === activeThreadId) ?? null,
    [threads, activeThreadId],
  );

  function openThread(id: string) {
    setActiveThreadId(id);
    setView('thread');
  }

  /* =================== RENDER =================== */
  if (view === 'compose') {
    return (
      <ComposeThread
        child={child}
        subjects={childSubjects}
        parentUid={parentUid}
        parentName={userData?.displayName ?? 'Parent'}
        parentEmail={userData?.email ?? ''}
        onCancel={() => setView('list')}
        onCreated={(id) => {
          setActiveThreadId(id);
          setView('thread');
        }}
      />
    );
  }

  if (view === 'thread' && activeThread) {
    return (
      <ThreadView
        thread={activeThread}
        parentUid={parentUid}
        parentName={userData?.displayName ?? 'Parent'}
        onBack={() => setView('list')}
      />
    );
  }

  // list view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          <MessageSquare size={16} /> Messages about {child.fullName}
        </h3>
        <AccentButton onClick={() => setView('compose')}>
          <span className="flex items-center gap-2">
            <Plus size={16} /> New message
          </span>
        </AccentButton>
      </div>

      {loading ? (
        <SoftCard>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 size={14} className="animate-spin" /> Loading messages…
          </div>
        </SoftCard>
      ) : threads.length === 0 ? (
        <SoftCard>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No messages yet. Use “New message” to contact the school about{' '}
            {child.fullName} — to request a teacher consultation, query marks,
            flag an incorrect detail, or request a details update.
          </p>
        </SoftCard>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => openThread(t.id)}
              className="w-full text-left"
            >
              <SoftCard className="transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {t.subject}
                      </span>
                      {t.unreadByParent && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {CATEGORY_LABELS[t.category]}
                    </p>
                    {t.lastMessagePreview && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 truncate">
                        {t.lastMessageSide === 'school' ? 'School: ' : 'You: '}
                        {t.lastMessagePreview}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={t.status} />
                </div>
              </SoftCard>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Status badge ---------------- */
export function StatusBadge({ status }: { status: ParentMessageThread['status'] }) {
  const map: Record<string, string> = {
    awaiting_school: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    awaiting_parent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    archived: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  };
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${map[status] ?? map.archived}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/* ===================================================================
   COMPOSE  (exported for reuse by the top-level Messages hub)
   =================================================================== */
interface ComposeProps {
  child: Student;
  subjects: Subject[];
  parentUid: string;
  parentName: string;
  parentEmail: string;
  onCancel: () => void;
  onCreated: (threadId: string) => void;
  /** Optional pre-fill (e.g. from the "add a child" reject flow). */
  initialCategory?: MessageCategory;
  initialSubject?: string;
  initialBody?: string;
}

export function ComposeThread({
  child,
  subjects,
  parentUid,
  parentName,
  parentEmail,
  onCancel,
  onCreated,
  initialCategory,
  initialSubject,
  initialBody,
}: ComposeProps) {
  const [category, setCategory] = useState<MessageCategory>(initialCategory ?? 'consultation');
  const [subjectContext, setSubjectContext] = useState('');
  const [subject, setSubject] = useState(initialSubject ?? '');
  const [messageText, setMessageText] = useState(initialBody ?? '');
  // detail-update structured changes
  const [changes, setChanges] = useState<ProposedChange[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const needsSubjectPicker = category === 'consultation' || category === 'marks_query';
  const isDetailUpdate = category === 'detail_update';

  function addChangeRow() {
    const first = CHANGEABLE_FIELDS[0];
    setChanges((prev) => [
      ...prev,
      {
        fieldKey: first.key,
        fieldLabel: first.label,
        currentValue: '',
        newValue: '',
      },
    ]);
  }
  function updateChange(idx: number, patch: Partial<ProposedChange>) {
    setChanges((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    );
  }
  function removeChange(idx: number) {
    setChanges((prev) => prev.filter((_, i) => i !== idx));
  }

  const valid =
    subject.trim().length > 0 &&
    (isDetailUpdate
      ? changes.length > 0 && changes.every((c) => c.newValue.trim().length > 0)
      : messageText.trim().length > 0);

  async function handleSubmit() {
    if (saving || !valid) return;
    setErr(null);
    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const subjObj = subjects.find((s) => s.id === subjectContext);

      const threadData: Record<string, unknown> = {
        studentId: child.id,
        studentName: child.fullName,
        parentUid,
        parentName,
        parentEmail,
        category,
        subject: subject.trim(),
        status: 'awaiting_school',
        createdAt: nowIso,
        updatedAt: nowIso,
        lastMessageAt: nowIso,
        lastMessageSide: 'parent',
        unreadBySchool: true,
        unreadByParent: false,
      };

      if (needsSubjectPicker && subjectContext) {
        threadData.subjectContext = subjectContext;
        threadData.subjectContextLabel = subjObj?.name ?? subjectContext;
      }

      let firstPostText = messageText.trim();

      if (isDetailUpdate) {
        threadData.proposedChanges = changes.map((c) => ({
          fieldKey: c.fieldKey,
          fieldLabel: c.fieldLabel,
          currentValue: c.currentValue ?? '',
          newValue: c.newValue.trim(),
        }));
        threadData.changeStatus = 'pending';
        // Compose a readable first post summarising the request
        firstPostText =
          'I would like to request the following detail update(s):\n' +
          changes
            .map((c) => `• ${c.fieldLabel}: ${c.newValue.trim()}`)
            .join('\n') +
          (messageText.trim() ? `\n\n${messageText.trim()}` : '');
      }

      threadData.lastMessagePreview = firstPostText.slice(0, 120);

      const threadRef = await addDoc(
        collection(db, 'reportParentMessages'),
        threadData,
      );

      // First post
      await addDoc(collection(db, 'reportParentMessages', threadRef.id, 'posts'), {
        authorUid: parentUid,
        authorName: parentName,
        authorSide: 'parent',
        text: firstPostText,
        createdAt: nowIso,
      });

      onCreated(threadRef.id);
    } catch (e) {
      setErr(
        e instanceof Error ? e.message : 'Could not send — please try again.',
      );
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onCancel}
        className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1"
      >
        <ChevronLeft size={16} /> Back to messages
      </button>

      <SoftCard>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-4">
          New message about {child.fullName}
        </h3>

        {err && (
          <div className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2 mb-3">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              What is this about?
            </label>
            <select
              className={glassFieldClass()}
              value={category}
              onChange={(e) => {
                setCategory(e.target.value as MessageCategory);
                setChanges([]);
              }}
            >
              {(Object.keys(CATEGORY_LABELS) as MessageCategory[]).map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>

          {needsSubjectPicker && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Subject {category === 'consultation' ? '(which teacher)' : ''}
              </label>
              <select
                className={glassFieldClass()}
                value={subjectContext}
                onChange={(e) => setSubjectContext(e.target.value)}
              >
                <option value="">— select subject —</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Subject line
            </label>
            <input
              type="text"
              className={glassFieldClass()}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                category === 'consultation'
                  ? 'e.g. Request to meet about progress'
                  : category === 'marks_query'
                    ? 'e.g. Question about Term 2 Maths mark'
                    : category === 'detail_correction'
                      ? 'e.g. Date of birth shows incorrectly'
                      : category === 'detail_update'
                        ? 'e.g. Updated home address'
                        : 'Brief subject'
              }
            />
          </div>

          {isDetailUpdate ? (
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Details to update
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                The school will review and apply these to {child.fullName}’s record.
              </p>
              <div className="space-y-2">
                {changes.map((c, idx) => {
                  const field = getChangeableField(c.fieldKey);
                  return (
                    <div
                      key={idx}
                      className="flex flex-col sm:flex-row gap-2 items-start sm:items-center"
                    >
                      <select
                        className={glassFieldClass('sm:w-1/2')}
                        value={c.fieldKey}
                        onChange={(e) => {
                          const f = getChangeableField(e.target.value);
                          updateChange(idx, {
                            fieldKey: e.target.value,
                            fieldLabel: f?.label ?? e.target.value,
                          });
                        }}
                      >
                        {CHANGEABLE_FIELDS.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type={field?.kind === 'tel' ? 'tel' : field?.kind === 'email' ? 'email' : 'text'}
                        className={glassFieldClass('flex-1')}
                        value={c.newValue}
                        onChange={(e) => updateChange(idx, { newValue: e.target.value })}
                        placeholder="New value"
                      />
                      <button
                        type="button"
                        onClick={() => removeChange(idx)}
                        className="text-slate-400 hover:text-red-500 text-xs px-2 py-2"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={addChangeRow}
                className="mt-2 text-sm text-primary hover:underline flex items-center gap-1"
              >
                <Plus size={14} /> Add a field to update
              </button>

              <div className="mt-4">
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Anything to add? (optional)
                </label>
                <textarea
                  className={glassFieldClass('min-h-[80px]')}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Optional note for the school"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Your message
              </label>
              <textarea
                className={glassFieldClass('min-h-[160px]')}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Write your message to the school…"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <AccentButton onClick={handleSubmit} disabled={!valid || saving}>
              {saving ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Sending…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send size={16} /> Send to school
                </span>
              )}
            </AccentButton>
          </div>
        </div>
      </SoftCard>
    </div>
  );
}

/* ===================================================================
   THREAD VIEW  (exported for reuse by the top-level Messages hub)
   =================================================================== */
interface ThreadViewProps {
  thread: ParentMessageThread;
  parentUid: string;
  parentName: string;
  onBack: () => void;
}

export function ThreadView({ thread, parentUid, parentName, onBack }: ThreadViewProps) {
  const [posts, setPosts] = useState<ParentMessagePost[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'reportParentMessages', thread.id, 'posts'),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const out: ParentMessagePost[] = [];
      snap.forEach((d) =>
        out.push({ id: d.id, ...(d.data() as Omit<ParentMessagePost, 'id'>) }),
      );
      setPosts(out);
    });
    return unsub;
  }, [thread.id]);

  // Clear the parent's unread flag when viewing
  useEffect(() => {
    if (thread.unreadByParent) {
      updateDoc(doc(db, 'reportParentMessages', thread.id), {
        unreadByParent: false,
      }).catch(() => {
        /* non-critical */
      });
    }
  }, [thread.id, thread.unreadByParent]);

  const canReply = thread.status === 'awaiting_school' || thread.status === 'awaiting_parent';

  async function sendReply() {
    if (sending || !reply.trim()) return;
    setErr(null);
    setSending(true);
    try {
      const nowIso = new Date().toISOString();
      await addDoc(collection(db, 'reportParentMessages', thread.id, 'posts'), {
        authorUid: parentUid,
        authorName: parentName,
        authorSide: 'parent',
        text: reply.trim(),
        createdAt: nowIso,
      });
      await updateDoc(doc(db, 'reportParentMessages', thread.id), {
        status: 'awaiting_school',
        updatedAt: nowIso,
        lastMessageAt: nowIso,
        lastMessageSide: 'parent',
        lastMessagePreview: reply.trim().slice(0, 120),
        unreadBySchool: true,
        unreadByParent: false,
      });
      setReply('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send reply.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1"
      >
        <ChevronLeft size={16} /> Back to messages
      </button>

      <SoftCard>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">
              {thread.subject}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {CATEGORY_LABELS[thread.category]}
              {thread.subjectContextLabel ? ` · ${thread.subjectContextLabel}` : ''}
            </p>
          </div>
          <StatusBadge status={thread.status} />
        </div>

        {/* Detail-change summary if present */}
        {thread.category === 'detail_update' && thread.proposedChanges && (
          <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
              <Pencil size={12} /> Requested detail changes
              {thread.changeStatus && (
                <span
                  className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${
                    thread.changeStatus === 'approved'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : thread.changeStatus === 'rejected'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                  }`}
                >
                  {thread.changeStatus}
                </span>
              )}
            </div>
            <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-300">
              {thread.proposedChanges.map((c, i) => (
                <li key={i}>
                  <span className="font-medium">{c.fieldLabel}:</span> {c.newValue}
                </li>
              ))}
            </ul>
          </div>
        )}
      </SoftCard>

      {/* Posts */}
      <div className="space-y-3">
        {posts.map((p) => {
          const mine = p.authorSide === 'parent';
          return (
            <div
              key={p.id}
              className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  mine
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100'
                }`}
              >
                <p className="text-xs font-semibold mb-0.5 opacity-80">
                  {mine ? 'You' : p.authorName}
                </p>
                <p className="text-sm whitespace-pre-wrap">{p.text}</p>
                <p className={`text-[10px] mt-1 ${mine ? 'text-white/60' : 'text-slate-400'}`}>
                  {new Date(p.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      {canReply ? (
        <SoftCard>
          {err && (
            <div className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2 mb-2">
              <AlertCircle size={14} /> {err}
            </div>
          )}
          <textarea
            className={glassFieldClass('min-h-[80px]')}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply…"
          />
          <div className="flex justify-end mt-2">
            <AccentButton onClick={sendReply} disabled={!reply.trim() || sending}>
              {sending ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Sending…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send size={16} /> Send reply
                </span>
              )}
            </AccentButton>
          </div>
        </SoftCard>
      ) : (
        <SoftCard>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            {thread.status === 'resolved' ? (
              <>
                <CheckCircle2 size={14} /> This conversation is marked resolved.
              </>
            ) : (
              <>
                <Clock size={14} /> This conversation is {STATUS_LABELS[thread.status].toLowerCase()}.
              </>
            )}
          </div>
        </SoftCard>
      )}
    </div>
  );
}
